import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { PickingApiAdapter } from '../adapters/PickingApiAdapter';
import type { Preparation, PreparationLine } from '../models/PreparationModels';

export type PreparationLineWithProduct = PreparationLine;
export type PreparationWithProducts = Preparation;

const PREPARATION_QUERY_KEY = 'picking-preparation';

export const preparationQueryKey = (id: number) => [PREPARATION_QUERY_KEY, id];

export const usePreparationQuery = (id: number) => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const adapter = new PickingApiAdapter(httpClient);

  const query = useQuery<Preparation>({
    queryKey: preparationQueryKey(id),
    queryFn: () => adapter.getPreparation(id),
    enabled: id > 0,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const updatePreparation = (updatedPreparation: Preparation) => {
    queryClient.setQueryData<Preparation>(preparationQueryKey(id), (prev) => {
      if (!prev) return updatedPreparation;
      const updatedLinesMap = new Map(updatedPreparation.lines.map((line) => [line.id, line]));
      const mergedLines = prev.lines.map((originalLine) => {
        const updatedLine = updatedLinesMap.get(originalLine.id);
        if (updatedLine) {
          return {
            ...originalLine,
            qty: updatedLine.qty,
            qty_done: updatedLine.qty_done,
            updated_at: updatedLine.updated_at,
            suggested_locations: originalLine.suggested_locations,
            other_locations: originalLine.other_locations,
          };
        }
        return originalLine;
      });
      return {
        ...updatedPreparation,
        lines: mergedLines,
        line_picks: updatedPreparation.line_picks ?? prev.line_picks,
      };
    });
  };

  const updateLineQuantity = (lineId: number, quantityToAdd: number) => {
    queryClient.setQueryData<Preparation>(preparationQueryKey(id), (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        lines: prev.lines.map((line) =>
          line.id === lineId ? { ...line, qty_done: Math.min(line.qty_done + quantityToAdd, line.qty) } : line,
        ),
      };
    });
  };

  return {
    preparation: query.data ?? null,
    loading: query.isLoading,
    error: query.error?.message ?? null,
    updatePreparation,
    updateLineQuantity,
  };
};
